import React, { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { fetchWithAuth } from '../../../utils/api';
import { PencilIcon, TrashIcon, PlusIcon, UserGroupIcon, UserIcon, LinkIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { useLocation } from 'react-router-dom';
import V3UserForm from './V3UserForm';
import { Heading } from '../../ui/heading';
import { Timestamp } from '../../ui/timestamp';
import { config } from '../../../config';

// User interface from the API
interface User {
  user_id: string;
  username: string;
  email: string;
  organization_id: string;
  role: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

// Form data interface for creating/updating users
interface UserFormData {
  user_id?: string;
  username: string;
  email: string;
  password?: string;
  organization_id: string;
  role: string;
  is_active?: boolean;
}

const V3UsersManagement: React.FC = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState<User | undefined>(undefined);
  const [magicLinkModal, setMagicLinkModal] = useState<{ isOpen: boolean; link: string; userEmail: string }>({
    isOpen: false,
    link: '',
    userEmail: ''
  });
  const { user: currentUser } = useAuth();
  const location = useLocation();
  
  // If we were passed an organization ID from the organization management page,
  // we'll filter users by that organization
  const filterOrgId = location.state?.orgId;
  
  useEffect(() => {
    fetchUsers();
  }, []);
  
  const fetchUsers = async () => {
    try {
      setLoading(true);
      let endpoint = '/v1/auth/admin/users';
      
      // If filtering by organization, add the organization_id parameter
      if (filterOrgId) {
        endpoint += `?organization_id=${filterOrgId}`;
      }
      
      const data = await fetchWithAuth(endpoint);
      setUsers(data);
      setError(null);
    } catch (err) {
      setError('Failed to load users. Please try again.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };
  
  const handleCreateUser = () => {
    setSelectedUser(undefined);
    setIsModalOpen(true);
  };
  
  const handleEditUser = (user: User) => {
    setSelectedUser(user);
    setIsModalOpen(true);
  };
  
  const handleDeleteUser = async (userId: string) => {
    if (!window.confirm('Are you sure you want to delete this user? This action cannot be undone.')) {
      return;
    }
    
    try {
      await fetchWithAuth(`/v1/auth/admin/users/${userId}`, {
        method: 'DELETE'
      });
      
      // Remove the user from the state without needing a server refresh
      setUsers(currentUsers => 
        currentUsers.filter(user => user.user_id !== userId)
      );
      
      // Show success message
      showSuccessMessage('User deleted successfully');
    } catch (err) {
      setError('Failed to delete user. Please try again.');
      console.error(err);
    }
  };
  
  const handleSubmitUser = async (userData: UserFormData) => {
    try {
      if (userData.user_id) {
        // Update existing user
        await fetchWithAuth(`/v1/auth/admin/users/${userData.user_id}`, {
          method: 'PUT',
          body: JSON.stringify(userData)
        });
      } else {
        // Create new user
        await fetchWithAuth('/v1/auth/admin/users', {
          method: 'POST',
          body: JSON.stringify(userData)
        });
      }
      
      // Close modal and refresh users
      setIsModalOpen(false);
      fetchUsers();
      showSuccessMessage(userData.user_id ? 'User updated successfully' : 'User created successfully');
    } catch (err) {
      throw err;
    }
  };
  
  const showSuccessMessage = (message: string) => {
    const tempMessage = document.createElement('div');
    tempMessage.className = 'fixed top-4 right-4 bg-green-50 text-green-800 px-4 py-3 rounded-lg shadow-lg border border-green-200 flex items-center space-x-2';
    tempMessage.innerHTML = `
      <svg class="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path>
      </svg>
      <span>${message}</span>
    `;
    document.body.appendChild(tempMessage);
    
    // Remove message after 3 seconds
    setTimeout(() => {
      tempMessage.remove();
    }, 3000);
  };
  
  const handleGenerateMagicLink = async (userId: string, userEmail: string) => {
    try {
      const response = await fetchWithAuth(`/v1/auth/admin/users/${userId}/magic-link`, {
        method: 'POST'
      });
      
      const data = await response;
      setMagicLinkModal({
        isOpen: true,
        link: data.magic_link,
        userEmail: userEmail
      });
    } catch (err) {
      setError('Failed to generate magic link. Please try again.');
      console.error(err);
    }
  };
  
  const copyMagicLinkToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(magicLinkModal.link);
      showSuccessMessage('Magic link copied to clipboard!');
    } catch (err) {
      console.error('Failed to copy to clipboard:', err);
    }
  };
  
  if (loading) {
    return (
      <div className="flex justify-center items-center h-96">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  if (error) {
    return (
      <div className="p-8">
        <div className="bg-red-50 border border-red-200 p-6 rounded-lg">
          <p className="text-red-800 mb-4">{error}</p>
          <button 
            onClick={fetchUsers}
            className="px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-md transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }
  
  return (
    <div className="flex-1 p-8">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <Heading level={2} className="text-gray-900 flex items-center">
              <UserGroupIcon className="w-7 h-7 mr-3 text-gray-700" />
              {filterOrgId ? 'Organization Users' : 'Users & Permissions'}
            </Heading>
            <p className="text-gray-600 mt-1">
              Manage user accounts and their access permissions
            </p>
          </div>
          <button 
            onClick={handleCreateUser}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg flex items-center hover:bg-blue-700 transition-colors shadow-sm"
          >
            <PlusIcon className="w-5 h-5 mr-2" />
            Add New User
          </button>
        </div>
        
        {/* Users Table */}
        <div className="bg-white shadow-sm rounded-lg border border-gray-200">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200">
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    User
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Role
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Created
                  </th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {users.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="px-6 py-12 text-center">
                      <UserIcon className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                      <p className="text-gray-500">No users found</p>
                    </td>
                  </tr>
                ) : (
                  users.map(user => (
                    <tr key={user.user_id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div>
                          <div className="text-sm font-medium text-gray-900">{user.username}</div>
                          <div className="text-sm text-gray-500">{user.email}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.role === 'admin' 
                            ? 'bg-purple-100 text-purple-800' 
                            : 'bg-blue-100 text-blue-800'
                        }`}>
                          {user.role}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          user.is_active 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          <div className={`w-1.5 h-1.5 rounded-full mr-1.5 ${
                            user.is_active ? 'bg-green-600' : 'bg-red-600'
                          }`} />
                          {user.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500">
                        <Timestamp value={user.created_at} format="absolute" />
                      </td>
                      <td className="px-6 py-4 text-right">
                        <div className="flex justify-end space-x-2">
                          <button 
                            className="p-2 rounded-lg hover:bg-blue-50 text-blue-600 hover:text-blue-700 transition-colors"
                            onClick={() => handleGenerateMagicLink(user.user_id, user.email)}
                            title="Generate magic link"
                          >
                            <LinkIcon className="w-4 h-4" />
                          </button>
                          <button 
                            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 hover:text-gray-900 transition-colors"
                            onClick={() => handleEditUser(user)}
                            title="Edit user"
                          >
                            <PencilIcon className="w-4 h-4" />
                          </button>
                          <button 
                            className={`p-2 rounded-lg transition-colors ${
                              user.username === currentUser?.username
                                ? 'text-gray-300 cursor-not-allowed'
                                : 'hover:bg-red-50 text-red-600 hover:text-red-700'
                            }`}
                            disabled={user.username === currentUser?.username}
                            onClick={() => user.username !== currentUser?.username && handleDeleteUser(user.user_id)}
                            title={user.username === currentUser?.username ? "You cannot delete your own account" : "Delete user"}
                          >
                            <TrashIcon className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
        
        {/* Footer Note */}
        <div className="mt-4 text-sm text-gray-500">
          <p>* You cannot delete your own account</p>
        </div>
      </div>
      
      {/* User Form Modal */}
      {isModalOpen && (
        <V3UserForm
          user={selectedUser}
          onSubmit={handleSubmitUser}
          onClose={() => setIsModalOpen(false)}
        />
      )}
      
      {/* Magic Link Modal */}
      {magicLinkModal.isOpen && (
        <>
          <div className="fixed inset-0 bg-black bg-opacity-50 z-40" onClick={() => setMagicLinkModal({ isOpen: false, link: '', userEmail: '' })} />
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
              <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
                <h2 className="text-lg font-semibold text-gray-900">
                  Magic Link Generated
                </h2>
                <button
                  onClick={() => setMagicLinkModal({ isOpen: false, link: '', userEmail: '' })}
                  className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                >
                  <XMarkIcon className="w-5 h-5 text-gray-500" />
                </button>
              </div>
              <div className="p-6">
                <p className="text-sm text-gray-600 mb-4">
                  Magic link for <strong>{magicLinkModal.userEmail}</strong>:
                </p>
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <code className="text-sm text-gray-800 break-all">
                    {magicLinkModal.link}
                  </code>
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    onClick={() => setMagicLinkModal({ isOpen: false, link: '', userEmail: '' })}
                    className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                  >
                    Close
                  </button>
                  <button
                    onClick={copyMagicLinkToClipboard}
                    className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    Copy Link
                  </button>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default V3UsersManagement;